import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUsers,
    faHashtag,
    faPlus,
    faPalette,
    faClock,
    faEuroSign,
} from "@fortawesome/free-solid-svg-icons";

import Collapse from "../../../general/auxiliar/collapse/Collapse";
import PaginatorComponent from "../../../general/auxiliar/ui/PaginatorComponent";
import CustomForm from "../../../general/form/CustomForm";
import EmptyTable from "../../../general/auxiliar/table/EmptyTable";
import LoadingTable from "../../../general/auxiliar/table/LoadingTable";
import OverviewTable from "../../../general/auxiliar/table/OverviewTable";
import usePaginationFilterEffects from "../../../../auxiliar/customHooks/usePaginationFilterEffects";
import useFormWithDisable from "../../../../auxiliar/customHooks/useFormWithDisable";
import OverviewCardList from "../../../general/auxiliar/table/OverviewCard";
import { colors } from "../../../../auxiliar/colors";
import { getAllClassTypes } from "../../../../actions/classTypes";
import { handleSubmit, getTableState } from "../../../../auxiliar/auxFunctions";

const ClassTypeCardList = ({ classTypes, navigate }) => {
    const renderTitle = (classType) => classType.name;

    const renderSubtitle = (classType) => (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm">
            <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faUsers} className="w-3 h-3 text-base-content/40" />
                {classType.number_participants} participantes
            </span>
            <span className="text-base-content/40">•</span>
            <span>{classType.session_duration} min</span>
            <span className="text-base-content/40">•</span>
            <span className="font-medium">{Number(classType.price).toFixed(2)}€</span>
        </div>
    );

    const renderFooter = (classType) => (
        <div className="flex items-center gap-2 text-xs sm:text-sm text-base-content/70">
            <div
                className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border border-base-300 flex-shrink-0"
                style={{ backgroundColor: classType.color }}
            ></div>
            <span className="truncate">{colors[classType.color]}</span>
        </div>
    );

    const getDetailPath = (id) => `/services/group-session-type/${id}`;

    return (
        <OverviewCardList
            data={classTypes}
            navigate={navigate}
            getDetailPath={getDetailPath}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
        />
    );
};

const ClassTypeTable = ({ classTypes, navigate }) => {
    const columns = [
        {
            label: "Nombre",
            icon: faUsers,
            width: "w-[30%]",
        },
        {
            label: "Participantes",
            icon: faHashtag,
            cellClass: "hidden sm:table-cell",
            width: "w-[14%]",
        },
        {
            label: "Duración",
            icon: faClock,
            cellClass: "hidden md:table-cell",
            width: "w-[14%]",
        },
        {
            label: "Precio",
            icon: faEuroSign,
            width: "w-[14%]",
        },
        {
            label: "Color",
            icon: faPalette,
            cellClass: "hidden lg:table-cell",
            width: "w-[14%]",
        },
    ];

    const getRowData = (classType, id) => [
        <div
            key={`${id}-name`}
            className="font-semibold text-base-content truncate"
            title={classType.name}
        >
            {classType.name}
        </div>,
        <span key={`${id}-participants`} className="text-base-content/70">
            {classType.number_participants}
        </span>,
        <span key={`${id}-duration`} className="text-base-content/70">
            {classType.session_duration} minutos
        </span>,
        <span key={`${id}-price`} className="text-base-content/70">
            {Number(classType.price).toFixed(2)}€
        </span>,
        <div key={`${id}-color`} className="flex items-center gap-2">
            <div
                className="w-6 h-6 rounded-full border-2 border-base-300 shadow-sm"
                style={{ backgroundColor: classType.color }}
            ></div>
            <span className="text-base-content/70 truncate">{colors[classType.color]}</span>
        </div>,
    ];

    const getDetailPath = (id) => `/services/group-session-type/${id}`;

    return (
        <OverviewTable
            data={classTypes}
            navigate={navigate}
            columns={columns}
            getRowData={getRowData}
            getDetailPath={getDetailPath}
            actionsWidth="w-[14%]"
        />
    );
};
const OverviewClassTypes = () => {
    const navigate = useNavigate();
    const { id: clinicId } = useSelector((state) => state.activeClinic);

    const { register, watch, errors } = useFormWithDisable();

    const [classTypes, setClassTypes] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        clinic_id: clinicId,
        order: "name",
        per_page: 10,
        page: 1,
    });

    const writtenFilter = {
        name: watch("name") ?? "",
    };

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllClassTypes,
        setClassTypes,
        setFilter,
    );

    const formConfig = {
        name: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Nombre",
            placeholder: "Nombre",
            name: "name",
            defaultValue: "",
        },
    };

    const tableState = getTableState(classTypes);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando tipos de clase..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faUsers}
                        title="No hay tipos de clase registrados"
                        description="Añade el primer tipo de clase para comenzar a configurar tus clases grupales."
                        buttonText="Nuevo tipo de clase"
                        onButtonClick={handleSubmit(
                            navigate,
                            "/services/group-session-type/register",
                        )}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <ClassTypeCardList classTypes={classTypes} navigate={navigate} />
                        <ClassTypeTable classTypes={classTypes} navigate={navigate} />
                    </>
                );
        }
    };

    return (
        <section>
            <h2 className="sr-only">
                Personaliza el formulario de historia clínica de tu clínica de fisioterapia en
                MiFisio Agenda - Historial clínico personalizado
            </h2>

            <Collapse isMainTitle={true} title="Tipos de Clase" faModality="filter">
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            <div className="mt-6">
                <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    {/* Action button */}
                    <button
                        className="btn btn-primary btn-sm w-full sm:w-auto"
                        onClick={handleSubmit(navigate, "/services/group-session-type/register")}
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Nuevo tipo de clase
                    </button>

                    {/* Pagination */}
                    <div className="w-full sm:w-auto">
                        <PaginatorComponent
                            pagination={classTypes.pagination}
                            setFilter={setFilter}
                            filter={filter}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {renderContent()}

                {/* Pagination for small screens - duplicated to improve UX */}
                <div className="w-full my-5 sm:w-auto lg:hidden">
                    <PaginatorComponent
                        pagination={classTypes.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                        showTotalItems={false}
                    />
                </div>
            </div>
        </section>
    );
};

export default OverviewClassTypes;
